import { StrictMode, useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { consumeUrlToken, currentUser, subscribe } from './identity.js';
import { openForUser } from './boot.js';
import { StoreContext } from './useStore.js';
import App from './ui/App.jsx';
import SignIn from './ui/SignIn.jsx';
import SetPassword from './ui/SetPassword.jsx';
import './styles.css';

function Root() {
  const [user, setUser] = useState(currentUser);
  const [session, setSession] = useState(null);   // { store, sync }
  const [status, setStatus] = useState({ phase: 'idle', pending: 0, lastSyncedAt: null, lastError: null });
  const [failure, setFailure] = useState(null);
  // `undefined` until the URL has been inspected. Rendering the sign-in screen
  // before then would flash a login at someone arriving from a confirmation
  // email, and discard the token in the fragment.
  const [arrival, setArrival] = useState(undefined);
  const opening = useRef(null);
  // Held in a ref as well as state: the teardown below runs from an effect
  // keyed on `user`, and reading `session` from that closure can hand back the
  // previous render's value — leaving an IndexedDB connection open, which then
  // blocks the next account from opening at all.
  const live = useRef(null);

  useEffect(() => subscribe(setUser), []);

  useEffect(() => {
    consumeUrlToken()
      .then(r => setArrival(r ?? null))
      .catch(err => setArrival({ kind: 'error', message: err.message }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      // Tear the previous account's store down before another can be opened,
      // so a second sign-in in the same tab never sees the first one's writing.
      live.current?.sync.stop();
      live.current?.store.close();
      live.current = null;
      opening.current = null;
      setSession(null);
      return;
    }
    if (opening.current === user.id) return;
    opening.current = user.id;

    openForUser(user, { onStatus: s => { if (!cancelled) setStatus(s); } })
      .then(opened => {
        if (cancelled) { opened.sync.stop(); opened.store.close(); return; }
        live.current = opened;
        setSession(opened);
      })
      .catch(err => { if (!cancelled) setFailure(err); });

    return () => { cancelled = true; };
    // Keyed on the id, not the object: every session write mints a fresh user
    // object, and re-running this would cancel the open already in progress.
  }, [user?.id]);

  const syncNow = useCallback(() => { session?.sync.sync().catch(() => {}); }, [session]);

  if (failure) {
    return (
      <div className="boot-error">
        <h1>Longhand couldn&rsquo;t open your writing.</h1>
        <p>{String(failure.message || failure)}</p>
        <p>Nothing has been lost. Reloading usually fixes it.</p>
      </div>
    );
  }
  if (arrival === undefined) return <div className="gate"><div className="gate-loading">One moment…</div></div>;

  if (user && (arrival?.kind === 'recovery' || arrival?.kind === 'invite')) {
    return (
      <SetPassword
        heading="New password"
        blurb={arrival.kind === 'invite'
          ? 'Welcome. Choose a password and your desk is ready.'
          : 'Choose a new password to finish signing in.'}
        onDone={() => setArrival(null)}
      />
    );
  }

  if (!user) return <SignIn arrival={arrival} />;
  if (!session) return <div className="gate"><div className="gate-loading">Opening your desk…</div></div>;

  return (
    <StoreContext.Provider value={session.store}>
      <App syncStatus={status} onSyncNow={syncNow} />
    </StoreContext.Provider>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode><Root /></StrictMode>
);
